import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";

interface ConnectivityState {
  isOnline: boolean;
  isReconnecting: boolean;
  lastOnlineAt: Date | null;
  /** Dev-only override */
  simulateOffline: boolean;
  setSimulateOffline: (v: boolean) => void;
}

const ConnectivityContext = createContext<ConnectivityState>({
  isOnline: true,
  isReconnecting: false,
  lastOnlineAt: null,
  simulateOffline: false,
  setSimulateOffline: () => {},
});

export const useConnectivity = () => useContext(ConnectivityContext);

const HEARTBEAT_INTERVAL = 30_000; // 30s
const HEARTBEAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/`;

export function ConnectivityProvider({ children }: { children: ReactNode }) {
  const [browserOnline, setBrowserOnline] = useState(navigator.onLine);
  const [serverReachable, setServerReachable] = useState(true);
  const [simulateOffline, setSimulateOffline] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [lastOnlineAt, setLastOnlineAt] = useState<Date | null>(navigator.onLine ? new Date() : null);
  const prevOnline = useRef(true);

  // Browser events
  useEffect(() => {
    const goOnline = () => setBrowserOnline(true);
    const goOffline = () => setBrowserOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Heartbeat
  const heartbeat = useCallback(async () => {
    if (!browserOnline) {
      setServerReachable(false);
      return;
    }
    try {
      const resp = await fetch(HEARTBEAT_URL, {
        method: "HEAD",
        cache: "no-store",
        headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
      });
      setServerReachable(resp.ok);
    } catch {
      setServerReachable(false);
    }
  }, [browserOnline]);

  useEffect(() => {
    heartbeat();
    const id = setInterval(heartbeat, HEARTBEAT_INTERVAL);
    return () => clearInterval(id);
  }, [heartbeat]);

  const isOnline = !simulateOffline && browserOnline && serverReachable;

  // Track reconnection state
  useEffect(() => {
    if (isOnline && !prevOnline.current) {
      setIsReconnecting(true);
      const t = setTimeout(() => setIsReconnecting(false), 5000);
      return () => clearTimeout(t);
    }
    if (isOnline) {
      setLastOnlineAt(new Date());
    }
    prevOnline.current = isOnline;
  }, [isOnline]);

  return (
    <ConnectivityContext.Provider value={{ isOnline, isReconnecting, lastOnlineAt, simulateOffline, setSimulateOffline }}>
      {children}
    </ConnectivityContext.Provider>
  );
}
